import React from "react";
import SuiBox from "components/SuiBox";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import MonthPicker from "components/MonthPicker";
import { Card, CircularProgress, Icon, Tooltip } from "@mui/material";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import { getUsersReq, getTravelAllowanceReportReq } from "services/api";
import {
  customSelectStyles,
  REPORT_BUTTON_SX,
  REPORT_TOOLBAR_SX,
  TRAVEL_ALLOWANCE_TABLE_COLUMNS,
  TRAVEL_ALLOWANCE_ELIGIBILITY_FILTER_OPTIONS,
} from "../../reports.constants";
import moment from "moment";

const formatMinutes = (totalMinutes) => {
  const h = Math.floor((totalMinutes || 0) / 60);
  const m = (totalMinutes || 0) % 60;
  return `${h}h ${m}m`;
};

const escapeCsvValue = (value) => {
  const stringValue = String(value ?? "");
  return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
};

function TravelAllowanceReport() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [reportRows, setReportRows] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "eligibleDays", direction: "desc" });
  const [eligibilityFilter, setEligibilityFilter] = useState(
    TRAVEL_ALLOWANCE_ELIGIBILITY_FILTER_OPTIONS.find((option) => option.value === "hide_zero")
  );

  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const admin_id = admin.id;
      const { data } = await getUsersReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
      });
      setUsers(data);
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsLoading(false);
  };

  const generateReport = async () => {
    if (!selectedMonth) return;
    setIsReportLoading(true);
    try {
      const admin_id = admin.id;
      const { data } = await getTravelAllowanceReportReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
        month: selectedMonth,
        user_id: selectedUser?.value,
      });
      setReportRows(data);
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsReportLoading(false);
  };

  useEffect(() => {
    getUsers();
    generateReport();
  }, []);

  const displayedRows = useMemo(() => {
    if (!reportRows) return reportRows;
    let rows = reportRows;
    if (eligibilityFilter.value === "hide_zero") {
      rows = reportRows.filter((row) => row.eligibleDays > 0);
    } else if (eligibilityFilter.value === "only_zero") {
      rows = reportRows.filter((row) => !row.eligibleDays);
    }
    if (sortConfig.key) {
      rows = [...rows].sort((a, b) => {
        const diff = (a[sortConfig.key] || 0) - (b[sortConfig.key] || 0);
        return sortConfig.direction === "asc" ? diff : -diff;
      });
    }
    return rows;
  }, [reportRows, sortConfig, eligibilityFilter]);

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: null };
    });
  };

  const exportToCsv = () => {
    if (!displayedRows || displayedRows.length === 0) return;

    const headers = [
      "Employee ID",
      "Engineer",
      ...TRAVEL_ALLOWANCE_TABLE_COLUMNS.map((column) => column.label),
    ];
    const rows = displayedRows.map((row) =>
      [
        row.employee_id,
        row.name,
        row.officeOnlyDays,
        row.wfhOnlyDays,
        row.hybridDays,
        formatMinutes(row.totalOfficeMinutes),
        row.eligibleDays,
        row.eligibleDays * 1.5,
      ]
        .map(escapeCsvValue)
        .join(",")
    );

    const csvContent = [headers.map(escapeCsvValue).join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `travel-allowance-report-${selectedMonth}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {errorMessage && (
        <ErrorAlert color="error" message={errorMessage} clearError={() => setErrorMessage(null)} />
      )}
      <SuiBox py={3}>
        <SuiBox
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          sx={REPORT_TOOLBAR_SX}
        >
          <SuiBox className="w-340" sx={{ height: "35px" }}>
            {isLoading ? (
              <CircularProgress color="dark" size={18} />
            ) : (
              <CustomSelect
                key={users.length}
                isSearchable={true}
                isClearable={true}
                customStyles={customSelectStyles}
                options={users}
                value={selectedUser}
                placeholder="All Engineers"
                onChange={(data) => setSelectedUser(data)}
              />
            )}
          </SuiBox>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          {reportRows && (
            <SuiBox sx={{ width: "190px", height: "35px" }}>
              <CustomSelect
                customStyles={customSelectStyles}
                options={TRAVEL_ALLOWANCE_ELIGIBILITY_FILTER_OPTIONS}
                value={eligibilityFilter}
                onChange={(data) => setEligibilityFilter(data)}
              />
            </SuiBox>
          )}
          <SuiButton
            variant="gradient"
            color="secondary"
            size="small"
            onClick={generateReport}
            sx={REPORT_BUTTON_SX}
          >
            Generate Report
            <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </SuiButton>
          {displayedRows && displayedRows.length > 0 && (
            <SuiButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={exportToCsv}
              sx={REPORT_BUTTON_SX}
            >
              Export CSV
              <Icon sx={{ fontWeight: "bold", ml: 0.5 }}>file_download</Icon>
            </SuiButton>
          )}
        </SuiBox>
      </SuiBox>

      <SuiBox pb={3}>
        {isReportLoading ? (
          <Card>
            <SuiBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={3}
            >
              <CircularProgress color="dark" size={30} />
            </SuiBox>
          </Card>
        ) : reportRows ? (
          <Card>
            <SuiBox p={3}>
              {displayedRows.length > 0 ? (
                <SuiBox sx={{ overflowX: "auto" }}>
                  <SuiBox
                    display="flex"
                    backgroundColor="#f8f9fa"
                    borderRadius="0.5rem 0.5rem 0 0"
                    p={2}
                    sx={{ borderBottom: "2px solid #dee2e6", minWidth: 800 }}
                  >
                    <SuiBox flex="1.5" px={1}>
                      <SuiTypography variant="button" fontWeight="bold" color="dark">
                        Engineer
                      </SuiTypography>
                    </SuiBox>
                    {TRAVEL_ALLOWANCE_TABLE_COLUMNS.map((column) => {
                      const isSorted = sortConfig.key === column.key;
                      const label = (
                        <SuiBox display="flex" alignItems="center" sx={{ gap: "2px" }}>
                          <SuiTypography variant="button" fontWeight="bold" color="dark">
                            {column.label}
                          </SuiTypography>
                          {column.sortable && (
                            <Icon
                              fontSize="small"
                              color={isSorted ? "info" : "secondary"}
                              sx={{ fontSize: "16px !important", opacity: isSorted ? 1 : 0.5 }}
                            >
                              {isSorted && sortConfig.direction === "desc"
                                ? "arrow_downward"
                                : isSorted && sortConfig.direction === "asc"
                                ? "arrow_upward"
                                : "unfold_more"}
                            </Icon>
                          )}
                        </SuiBox>
                      );
                      return (
                        <SuiBox
                          key={column.key}
                          flex="1"
                          px={1}
                          sx={column.sortable ? { cursor: "pointer" } : undefined}
                          onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                        >
                          {column.tooltip ? (
                            <Tooltip title={column.tooltip} placement="top">
                              {label}
                            </Tooltip>
                          ) : (
                            label
                          )}
                        </SuiBox>
                      );
                    })}
                  </SuiBox>

                  <SuiBox sx={{ minWidth: 800 }}>
                    {displayedRows.map((row, index) => (
                      <SuiBox
                        key={row.user_slack_id}
                        display="flex"
                        p={2}
                        sx={{
                          borderBottom:
                            index < displayedRows.length - 1 ? "1px solid #dee2e6" : "none",
                          "&:hover": { backgroundColor: "#f8f9fa" },
                        }}
                      >
                        <SuiBox flex="1.5" px={1}>
                          <SuiBox
                            component="a"
                            href={`/reports/general-report?user=${encodeURIComponent(
                              row.user_slack_id
                            )}&month=${selectedMonth}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            display="block"
                            width="fit-content"
                            sx={{
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            <SuiTypography variant="button" fontWeight="medium" color="black">
                              {row.name}
                            </SuiTypography>
                          </SuiBox>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="text">
                            {row.officeOnlyDays}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="text">
                            {row.wfhOnlyDays}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="text">
                            {row.hybridDays}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="text">
                            {formatMinutes(row.totalOfficeMinutes)}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="black" fontWeight="bold">
                            {row.eligibleDays}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="caption" color="black" fontWeight="bold">
                            {row.eligibleDays * 1.5}
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                    ))}
                  </SuiBox>
                </SuiBox>
              ) : (
                <SuiBox textAlign="center" py={4}>
                  <Icon fontSize="large" color="error">
                    report
                  </Icon>
                  <SuiTypography variant="button" color="text" display="block" mt={1}>
                    {eligibilityFilter.value !== "all" && reportRows.length > 0
                      ? "No engineers match the selected filter"
                      : "No records found"}
                  </SuiTypography>
                </SuiBox>
              )}
            </SuiBox>
          </Card>
        ) : null}
      </SuiBox>
    </>
  );
}

export default TravelAllowanceReport;
